import React, { useContext, useState, useEffect, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  Form,
  ButtonsBlock,
  Button,
  TextField,
  UtilsBlock,
  Checkbox,
  Dropdown,
  ErrorInlineFeedback,
  Spinner,
} from '@hz-design-system/web-ui';
import { dropDownOptions } from '../../../constants/constants';
import errorTypes from '../../../constants/formErrorTypes';
import {
  validatePostCode,
  validateAddressMinLength,
  validateAddressMaxLength,
  validateHouseNumber,
  validateTaxNumber,
} from '../../../utils/validations';
import trackEvent from '../../../utils/trackEvent';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import { stepLabels, FORM_NEXT_STEP_CLICK, FORM_ERROR_TC_NOT_ACCEPTED } from '../../../constants/gaConstants';

import Classes from './AdmarktRegistrationStepInvoice.module.scss';

type AdmarktRegistrationStepInvoiceProps = {
  store: Record<string, any>;
  onSubmit: (data: any) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
};

const AdmarktRegistrationStepInvoice = ({ store, onSubmit, onChange }: AdmarktRegistrationStepInvoiceProps) => {
  const i18n = useI18nContext();
  const [isSendInvoiceChecked, setIsSendInvoiceChecked] = useState(false);
  const [isTAndCChecked, setIsTAndCChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    links: { privacyPolicyUrl },
  } = useContext<{ links: Record<string, string> }>(EnvironmentContext as any);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: 'onTouched', defaultValues: store });

  const { ref: streetNameRef, ...streetNameRest } = register('streetName', {
    required: true,
    validate: {
      minLength: (value) => validateAddressMinLength(value),
      maxLength: (value) => validateAddressMaxLength(value),
    },
  });

  const { ref: houseNumberRef, ...houseNumberRest } = register('houseNumber', {
    required: true,
    validate: (value) => validateHouseNumber(value),
  });

  const { ref: postalCodeRef, ...postalCodeRest } = register('postalCode', {
    required: true,
    validate: (value) => validatePostCode(value),
  });

  const { ref: cityRef, ...cityRest } = register('city', {
    required: true,
    validate: {
      minLength: (value) => validateAddressMinLength(value),
      maxLength: (value) => validateAddressMaxLength(value),
    },
  });

  const { ref: taxNumberRef, ...taxNumberRest } = register('taxNumber', {
    validate: (value) => validateTaxNumber(value),
  });

  useEffect(() => {
    reset(store);
  }, [reset, store]);

  useEffect(() => {
    if (isSendInvoiceChecked && isTAndCChecked) {
      clearErrors(errorTypes.terms);
    }
  }, [clearErrors, isSendInvoiceChecked, isTAndCChecked]);

  const addressErrors = (fieldId: string, fieldName: string) => {
    let errorMessage;
    if (errors[fieldId] && errors[fieldId]?.type === errorTypes.minLength) {
      errorMessage = i18n.t('admarktRegistration.step.field.error.too_short', { fieldName });
    } else if (errors[fieldId] && errors[fieldId]?.type === errorTypes.maxLength) {
      errorMessage = i18n.t('admarktRegistration.step.field.error.too_long', { fieldName });
    }
    return errorMessage;
  };

  const onBeforeSubmit = (data: any) => {
    trackEvent({ eventAction: FORM_NEXT_STEP_CLICK, eventLabel: stepLabels.STEP3 });
    setIsLoading(true);
    if (isSendInvoiceChecked && isTAndCChecked) {
      onSubmit(data);
    } else {
      trackEvent({ eventAction: FORM_ERROR_TC_NOT_ACCEPTED, eventLabel: stepLabels.STEP3 });
      setError(errorTypes.terms, {
        type: errorTypes.terms,
        message: i18n.t('admarktRegistration.step.invoice.error.terms'),
      });
      setIsLoading(false);
    }
  };

  return (
    <section className={Classes.root}>
      <UtilsBlock pb="s" pt="l">
        <Trans tagName="h3" i18nKey="admarktRegistration.step.invoice.title" name={store.firstName} />
      </UtilsBlock>
      <UtilsBlock pb="l" textStyle="body-regular">
        <Trans tagName="p" i18nKey="admarktRegistration.step.invoice.info_text" />
      </UtilsBlock>
      <Form onSubmit={handleSubmit(onBeforeSubmit)}>
        <div className={Classes.columns}>
          <div className={Classes.columnWide}>
            <TextField
              id="streetName"
              labelText={i18n.t('admarktRegistration.step.invoice.label.street_name')}
              isRequired
              inputRef={streetNameRef}
              {...streetNameRest}
              errorText={addressErrors('streetName', i18n.t('admarktRegistration.step.invoice.label.street_name'))}
              onChange={onChange}
            />
          </div>
          <div className={Classes.columnNarrow}>
            <TextField
              id="houseNumber"
              labelText={i18n.t('admarktRegistration.step.invoice.label.house_number')}
              isRequired
              inputRef={houseNumberRef}
              {...houseNumberRest}
              errorText={
                errors?.houseNumber?.type === 'validate'
                  ? i18n.t('admarktRegistration.step.field.house_number_error', {
                      fieldName: i18n.t('admarktRegistration.step.contact.label.house_number'),
                    })
                  : undefined
              }
              onChange={onChange}
            />
          </div>
        </div>
        <div className={Classes.columns}>
          <div className={Classes.columnWide}>
            <div className={Classes.subColumns}>
              <div className={Classes.subColumnNarrow}>
                <TextField
                  id="postalCode"
                  labelText={i18n.t('admarktRegistration.step.invoice.label.postal_code')}
                  isRequired
                  inputRef={postalCodeRef}
                  {...postalCodeRest}
                  errorText={
                    errors?.postalCode?.type === 'validate'
                      ? i18n.t('admarktRegistration.step.invoice.error.postal_code')
                      : undefined
                  }
                  onChange={onChange}
                />
              </div>
              <div className={Classes.subColumnWide}>
                <TextField
                  id="city"
                  labelText={i18n.t('admarktRegistration.step.invoice.label.city')}
                  isRequired
                  inputRef={cityRef}
                  {...cityRest}
                  errorText={addressErrors('city', i18n.t('admarktRegistration.step.invoice.label.city'))}
                  onChange={onChange}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '6px' }} className={Classes.columnNarrow}>
            <Dropdown
              id="country"
              labelText={i18n.t('admarktRegistration.step.invoice.label.country')}
              options={dropDownOptions}
              disabled={true}
              onChange={onChange}
            />
          </div>
        </div>
        <div className={Classes.columns}>
          <div className={Classes.columnFullWidth}>
            <TextField
              id="taxNumber"
              labelText={i18n.t('admarktRegistration.step.invoice.label.taxNumber')}
              optionalText={i18n.t('admarktRegistration.step.field.optional_text')}
              inputRef={taxNumberRef}
              {...taxNumberRest}
              errorText={
                errors?.taxNumber?.type === 'validate'
                  ? i18n.t('admarktRegistration.step.invoice.error.taxNumber')
                  : undefined
              }
              onChange={onChange}
            />
          </div>
        </div>
        <UtilsBlock
          pb="xxs"
          pt="xxs"
          className={Classes.checkbox_container}
          onClick={() => setIsSendInvoiceChecked(!isSendInvoiceChecked)}
        >
          <Checkbox
            value="sendInvoice"
            id="sendInvoice"
            labelText=""
            isChecked={isSendInvoiceChecked}
            onChange={() => setIsSendInvoiceChecked(!isSendInvoiceChecked)}
          />
          <Trans tagName="span" i18nKey="admarktRegistration.step.invoice.label.send_invoice" />
        </UtilsBlock>
        <UtilsBlock
          pb="xxs"
          pt="xxs"
          className={Classes.checkbox_container}
          onClick={() => setIsTAndCChecked(!isTAndCChecked)}
        >
          <Checkbox
            value="acceptTAndC"
            id="acceptTAndC"
            labelText=""
            isChecked={isTAndCChecked}
            onChange={() => setIsTAndCChecked(!isTAndCChecked)}
          />
          <Trans tagName="span" i18nKey="admarktRegistration.step.invoice.label.terms" url={privacyPolicyUrl} />
        </UtilsBlock>
        {errors.terms && <ErrorInlineFeedback icon="alert">{errors.terms.message as ReactNode}</ErrorInlineFeedback>}
        {isLoading ? (
          <Spinner />
        ) : (
          <ButtonsBlock kind="adaptive">
            <Button.Primary data-testid="next">{i18n.t('admarktRegistration.step.button.next')}</Button.Primary>
          </ButtonsBlock>
        )}
      </Form>
    </section>
  );
};

export default AdmarktRegistrationStepInvoice;
